import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  Button,
  Form,
  Input,
  Select,
  Radio,
  DatePicker,
  Space,
  TreeSelect,
  Tree,
  Upload,
  Cascader,
  TimePicker
} from "antd";
import styles from "../../static/css/index.module.css";
import {
  getFirstDay,
  getStorage,
  getTimeFormat,
  setIcons,
} from "../../utils/common";
import type { FormItemProps } from "antd";
import "dayjs/locale/zh-cn";
import locale from "antd/es/date-picker/locale/zh_CN";
import dayjs, { Dayjs } from "dayjs";
import { FormLayout } from "antd/es/form/Form";
import { SpaceSize } from "antd/es/space";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { FormInstance } from "antd/lib";
import { setType } from "./helps";

const { RangePicker } = DatePicker;
const { TextArea } = Input;

const quillModules = {
  toolbar: {
    container: [
      ["bold", "italic", "underline", "strike"],
      ["blockquote", "code-block"],
      [{ header: 1 }, { header: 2 }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ script: "sub" }, { script: "super" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ direction: "rtl" }],
      [{ size: ["small", false, "large", "huge"] }],
      [{ color: [] }],
      [{ background: [] }],
      [{ font: [] }],
      [{ align: [] }],
      ["link", "image",],
    ],
  },
};
interface FormTabItem extends FormItemProps {
  type: string;
  width?: string;
  options?: any[];
  params?: Record<string, any>;
  notShow?: Boolean;
  showCondition?: string;
  show?: boolean; // 新增：直接控制显示/隐藏
  span?: number; // 新增：跨列数，用于多列布局
  _isFullWidth?: boolean; // 内部使用：标记全宽字段
  _isPlaceholder?: boolean; // 内部使用：标记占位符
  defaultValues?: any[]; // rangePicker 默认值
}

// 评估显示条件的函数
const evaluateCondition = (condition: string, formValues: Record<string, any>): boolean => {
  if (!condition) return true;
  
  try {
    // 创建一个安全的评估环境，只允许访问表单值
    const context = { ...formValues };
    
    // 将条件字符串中的变量替换为实际值
    let evalString = condition;
    Object.keys(context).forEach(key => {
      const value = context[key];
      const regex = new RegExp(`\\b${key}\\b`, 'g');
      if (typeof value === 'string') {
        evalString = evalString.replace(regex, `"${value}"`);
      } else if (value !== null && value !== undefined) {
        evalString = evalString.replace(regex, JSON.stringify(value));
      } else {
        evalString = evalString.replace(regex, 'null');
      }
    });
    
    // 使用 Function 构造函数安全地评估表达式
    return new Function('return ' + evalString)();
  } catch (error) {
    console.warn('条件评估失败:', condition, error);
    return true; // 如果评估失败，默认显示
  }
};

const IFormTab = (props: {
  content?: { data: string | number; type: string };
  formTab: FormTabItem[];
  subFoot?: boolean;
  search?: Function | undefined;
  reset?: React.FormEventHandler<HTMLFormElement> | undefined;
  footer?: any[];
  initialValues?: Record<string, any> | undefined;
  layout?: FormLayout;
  size?: SpaceSize | [SpaceSize, SpaceSize];
  gap?: string;
  defaultTimer?: any;
  form?: FormInstance;
  showFooter?: boolean;
  loading?: boolean;
  confirmLoading?: boolean;
  confirm?: Function | undefined;
  columns?: number; // 新增：支持多列布局
}) => {
  const {
    formTab,
    subFoot,
    search,
    reset,
    footer,
    initialValues,
    layout = "inline",
    size = [8, 16],
    gap = "12px 10px",
    defaultTimer = [
      dayjs(getFirstDay(new Date())),
      dayjs(getTimeFormat(new Date()), "YYYY-MM-DD"),
    ],
    showFooter = true,
    form: formProps,
    loading,
    confirmLoading,
    columns = 1, // 默认单列
  } = props;

  const [form] = Form.useForm();
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const formTabRef = useRef(formTab);

  const finalForm = formProps || form;

  // 更新 formTab 引用
  useEffect(() => {
    formTabRef.current = formTab;
  }, [formTab]);

  // 初始化表单值
  useEffect(() => {
    if (initialValues) {
      console.log('PageTab 初始化表单值:', initialValues);
      const decodedValues = { ...initialValues };
  
      // 遍历表单项，根据类型对数据进行解码
      formTabRef.current.forEach((item) => {
        if (subFoot) {
          item.labelCol =  item.labelCol || { span: 8, offset: 0 }
        }
        if (item.type === "quill" && initialValues[item.name as string]) {
          try {
            // Base64 解码并解码 URI 编码
            const encodedContent = initialValues[item.name as string];
 
            decodedValues[item.name as string] = decodeURIComponent(encodedContent);
          } catch (error) {
            console.error(`解码失败: ${item.name}`, error);
          }
        }
      });
  
      console.log('PageTab 设置表单值:', decodedValues);
      finalForm.setFieldsValue(decodedValues);
      setFormValues(decodedValues);
    }
  }, [initialValues, finalForm, subFoot]); // 移除 formTab 依赖，避免重复初始化

  // 设置默认时间范围
  useEffect(() => {
    if (!finalForm.getFieldsValue().rangePicker) {
      // 查找 rangePicker 类型的表单项，检查是否有 defaultValues
      const rangePickerItem = formTab.find(item => item.type === 'rangePicker');
      if (rangePickerItem?.defaultValues) {
        const convertedTimer = [
          dayjs(rangePickerItem.defaultValues[0]),
          dayjs(rangePickerItem.defaultValues[1])
        ];
        finalForm.setFieldsValue({
          rangePicker: convertedTimer
        });
        console.log("设置 rangePicker 的默认值 (from defaultValues): ", convertedTimer);
      } else if (defaultTimer) {
        // 确保 defaultTimer 是正确的 dayjs 对象格式
        const convertedTimer = Array.isArray(defaultTimer) 
          ? defaultTimer.map(date => dayjs(date))
          : defaultTimer;
        
        finalForm.setFieldsValue({
          rangePicker: convertedTimer
        });
        console.log("设置 rangePicker 的默认值: ", convertedTimer);
      }
    }
  }, [defaultTimer, finalForm, formTab]);

  // 监听表单值变化
  const onValuesChange = useCallback((changedValues: any, allValues: any) => {
    setFormValues(allValues);
  }, []);

  // 使用 useMemo 优化过滤后的表单项
  const filteredFormTab = useMemo(() => {
    const filtered = formTab
      ?.filter((i) => !i?.notShow)
      ?.filter((item) => {
        // 评估显示条件
        if (item.showCondition) {
          return evaluateCondition(item.showCondition, formValues);
        }
        // 处理 show 参数
        if (item.show !== undefined) {
          return item.show;
        }
        return true;
      });
    return filtered;
  }, [formTab, formValues]);

  const onFinish = (values: any) => {
    const formData = {
      ...initialValues,
      ...values,
    };

    // Handle date picker values
    if (values?.datePicker) {
      values.datePicker = dayjs(values.datePicker);
    }
    if (values?.rangePicker && values?.rangePicker[0]) {
      const startTime = dayjs(values.rangePicker[0]).format("YYYY-MM-DD 00:00:00");
      const endTime = dayjs(values.rangePicker[1]).format("YYYY-MM-DD 23:59:59");
      delete formData.rangePicker;
      formData.startTime = startTime;
      formData.endTime = endTime;
    } else {
      delete formData.rangePicker;
      formData.startTime = "";
      formData.endTime = "";
    }

    // Handle rich text fields - encrypt content
    formTab.forEach(item => {
      if (item.type === 'quill' && formData[item.name as string]) {
        let content = formData[item.name as string].toString();

        content = encodeURIComponent(content);

        formData[item.name as string] = content;
      }   
    });
  
    console.log('处理后的表单数据：', formData);
    search && search(formData);
  };

  // 将表单项分组为多列，支持跨列字段
  const { regularItems, fullWidthItems } = useMemo(() => {
    if (columns === 1) {
      return { regularItems: [filteredFormTab], fullWidthItems: [] };
    }
    
    const regularItems: FormTabItem[] = [];
    const fullWidthItems: FormTabItem[] = [];
    
    filteredFormTab?.forEach((item) => {
      const span = item.span || 1;
      
      if (span >= columns) {
        // 跨列字段单独处理
        fullWidthItems.push(item);
      } else {
        // 普通字段
        regularItems.push(item);
      }
    });
    
    // 将普通字段分组到列中
    const groups: FormTabItem[][] = [];
    for (let i = 0; i < columns; i++) {
      groups.push([]);
    }
    
    regularItems.forEach((item, index) => {
      const columnIndex = index % columns;
      groups[columnIndex].push(item);
    });
    
    return { regularItems: groups, fullWidthItems };
  }, [filteredFormTab, columns]);

  return (
    <>
      <Form
        form={finalForm}
        onFinish={onFinish}
        onReset={reset}
        onValuesChange={onValuesChange}
        layout={layout}
        // wrapperCol={{ span: 0, offset: 0 }}
        style={{ gap: gap }}
      >
        {columns === 1 ? (
          // 单列布局
          filteredFormTab
            ?.map((item, index) => {
              // 从 item 中提取 showCondition，避免传递给 DOM
              const { showCondition, show, defaultValues, ...formItemProps } = item;
              return (
                <Form.Item
                  // labelCol={ !subFoot ? { span: 6, offset: 0 } : { span: 8, offset: 0 }}
                  style={{ width: item.width || "260px", marginBottom: layout === "horizontal" ? "10px" : "auto" }}
                  className={styles.formItem}
                  {...formItemProps}
                  key={`${item.name || `form-item-${index}`}-${item.show !== undefined ? item.show : 'always'}`}
                >
                  {setType(item, initialValues, defaultTimer, () => {
                    finalForm.setFieldsValue({
                      rangePicker: []
                    });
                  })}
                </Form.Item>
              );
            })
        ) : (
          // 多列布局
          <div>
            {/* 普通字段 - 多列布局 */}
            <div style={{ display: "grid", gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: "16px" }}>
              {regularItems.map((columnItems, columnIndex) => (
                <div key={columnIndex}>
                  {columnItems.map((item, index) => {
                    // 从 item 中提取 showCondition，避免传递给 DOM
                    const { showCondition, show, defaultValues, ...formItemProps } = item;
                    return (
                      <Form.Item
                        style={{ 
                          width: item.width || "100%", 
                          marginBottom: layout === "horizontal" ? "10px" : "auto"
                        }}
                        className={styles.formItem}
                        {...formItemProps}
                        key={`${item.name || `form-item-${columnIndex}-${index}`}-${item.show !== undefined ? item.show : 'always'}`}
                      >
                        {setType(item, initialValues, defaultTimer, () => {
                          finalForm.setFieldsValue({
                            rangePicker: []
                          });
                        })}
                      </Form.Item>
                    );
                  })}
                </div>
              ))}
            </div>
            
            {/* 跨列字段 - 单独渲染 */}
            {fullWidthItems.map((item, index) => {
              // 从 item 中提取 showCondition，避免传递给 DOM
              const { showCondition, show, defaultValues, ...formItemProps } = item;
              return (
                <Form.Item
                  style={{ 
                    width: item.width || "100%", 
                    marginBottom: layout === "horizontal" ? "10px" : "auto",
                    marginTop: "16px"
                  }}
                  className={styles.formItem}
                  {...formItemProps}
                  key={`${item.name || `form-item-full-${index}`}-${item.show !== undefined ? item.show : 'always'}`}
                >
                  {setType(item, initialValues, defaultTimer, () => {
                    finalForm.setFieldsValue({
                      rangePicker: []
                    });
                  })}
                </Form.Item>
              );
            })}
          </div>
        )}
       {
        showFooter && (
          <>
          {!subFoot && (
            <div>
              <Space size={size}>
                <Form.Item>
                  <Button type="primary" htmlType="submit" loading={loading}>
                    搜索
                  </Button>
                </Form.Item>
                <Form.Item>
                  <Button htmlType="reset" type="default">
                    重置
                  </Button>
                </Form.Item>
              </Space>
            </div>
          )}
          {subFoot && (
            <div className={styles.modalForm}>
              <Space>
                <Form.Item>
                  <Button htmlType="reset" type="default">
                    取消
                  </Button>
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit" loading={confirmLoading}>
                    确认
                  </Button>
                </Form.Item>
              </Space>
            </div>
          )}
        </>
       )
      }
      </Form>
      <Space align="start" className={styles.pageFooter}>
        {footer?.map((item, index) => {
          return item.render ? (
            <div key={index}>
            {item.render}
            </div>
          ) : (
            <Button {...item} key={index}>
              {item.title}
            </Button>
          );
        })}
      </Space>
    </>
  );
};
export default IFormTab;
